<p *ngIf="loading() && !overview()" class="m-0 py-8 text-center opacity-75">Loading all integrations...</p>

<div *ngIf="failed() && !loading()" data-overview-failed class="flex flex-col items-start gap-1 rounded-lg border border-[var(--app-border)] p-4">
  <span>Could not load all integrations.</span>
  <button type="button" (click)="retry.emit()" class="min-h-11 font-semibold text-[var(--primary-dark)] hover:underline">Try again</button>
</div>

<div *ngIf="overview() as o" class="flex flex-col gap-5">
  <div class="flex flex-wrap items-center justify-between gap-2">
    <div class="flex flex-wrap gap-2" data-counts>
      <span *ngFor="let c of counts()" class="rounded-full border px-2.5 py-0.5 text-xs font-semibold" [ngClass]="pillClasses(c.status)">{{ c.text }}</span>
    </div>
    <span class="text-xs">Checked {{ o.checkedAt | drawerTime }}</span>
  </div>

  <section class="flex flex-col gap-2" data-section="fms">
    <div role="heading" aria-level="3" class="text-sm font-semibold text-[var(--primary-dark)]">FMS (Crest) · nightly 00:00</div>
    <ul class="m-0 list-none rounded-lg border border-[var(--app-border)] p-0">
      <li *ngFor="let r of fmsRows()" class="flex items-center justify-between gap-3 border-b border-[var(--app-border)] px-3.5 py-2.5 last:border-b-0">
        <div class="flex min-w-0 flex-col gap-0.5">
          <span class="break-words font-semibold">{{ r.source.label }}</span>
          <span *ngIf="r.forClients" class="text-xs opacity-75">For {{ r.forClients }}</span>
          <span class="text-xs">{{ r.source.pull.reason | drawerTime:'text' }}<ng-container *ngIf="r.source.newestSpray"> · newest spray {{ r.source.newestSpray | date:DAY }}</ng-container></span>
        </div>
        <app-integration-pill class="shrink-0" [status]="r.source.pull.status" [help]="true"></app-integration-pill>
      </li>
    </ul>
  </section>

  <section class="flex flex-col gap-2" data-section="igator">
    <div role="heading" aria-level="3" class="text-sm font-semibold text-[var(--primary-dark)]">iGator irrigation · daily 08:00</div>
    <p *ngIf="siteRows().length === 0" class="m-0 text-xs opacity-75">No iGator systems are pulled.</p>
    <ul *ngIf="siteRows().length > 0" class="m-0 list-none rounded-lg border border-[var(--app-border)] p-0">
      <li *ngFor="let r of siteRows()" class="border-b border-[var(--app-border)] last:border-b-0">
        <div class="flex items-center justify-between gap-3 px-3.5 py-2.5">
          <div class="flex min-w-0 flex-col gap-0.5">
            <span class="break-words font-semibold">Site {{ r.site.siteId || 'unknown' }}<ng-container *ngIf="r.forClients"> · {{ r.forClients }}</ng-container></span>
            <span class="break-words text-xs">{{ r.site.reason | drawerTime:'text' }}<ng-container *ngIf="r.site.newestRunAt"> · newest run {{ r.site.newestRunAt | drawerTime }}</ng-container></span>
            <button type="button" (click)="toggleSite(r.key)" [attr.aria-expanded]="expanded().has(r.key)"
                    class="min-h-8 self-start text-xs font-semibold text-[var(--primary-dark)] hover:underline">
              {{ expanded().has(r.key) ? 'Hide' : 'Show' }} {{ r.site.systems.length }} {{ r.site.systems.length === 1 ? 'system' : 'systems' }}
            </button>
          </div>
          <app-integration-pill class="shrink-0" [status]="r.site.status" [help]="true"></app-integration-pill>
        </div>
        <ul *ngIf="expanded().has(r.key)" class="m-0 list-none border-t border-[var(--app-border)] bg-[var(--app-base)] p-0">
          <li *ngFor="let sys of r.site.systems" class="flex items-center justify-between gap-3 px-3.5 py-1.5 text-xs">
            <span class="min-w-0 break-words"><strong>{{ sys.storedName }}</strong> · {{ sys.pull.reason | drawerTime:'text' }}</span>
            <app-integration-pill class="shrink-0" [status]="sys.pull.status"></app-integration-pill>
          </li>
        </ul>
      </li>
    </ul>
  </section>

  <section class="flex flex-col gap-2" data-section="weather">
    <div role="heading" aria-level="3" class="text-sm font-semibold text-[var(--primary-dark)]">Weather (Pessl)</div>
    <ul class="m-0 list-none rounded-lg border border-[var(--app-border)] p-0">
      <li *ngFor="let r of stationRows()" class="flex items-center justify-between gap-3 border-b border-[var(--app-border)] px-3.5 py-2.5 last:border-b-0">
        <div class="flex min-w-0 flex-col gap-0.5">
          <span class="break-words font-semibold">{{ r.name }}</span>
          <span *ngIf="r.forClients" class="text-xs opacity-75">For {{ r.forClients }}</span>
          <span class="break-words text-xs">{{ r.row.reason | drawerTime:'text' }}</span>
        </div>
        <app-integration-pill class="shrink-0" [status]="r.row.status" [help]="true" [weather]="true"></app-integration-pill>
      </li>
      <li *ngIf="withoutStation()" data-no-station class="flex items-center justify-between gap-3 px-3.5 py-2.5">
        <div class="flex min-w-0 flex-col gap-0.5">
          <span class="font-semibold">No station linked</span>
          <span class="break-words text-xs">{{ withoutStation() }}</span>
        </div>
        <app-integration-pill class="shrink-0" [status]="'NOT_SET_UP'" [help]="true"></app-integration-pill>
      </li>
    </ul>
  </section>

  <section class="flex flex-col gap-2" data-section="tenants">
    <div role="heading" aria-level="3" class="text-sm font-semibold text-[var(--primary-dark)]">Linked fields per client</div>
    <div class="overflow-x-auto rounded-lg border border-[var(--app-border)]">
      <table class="w-full border-collapse text-[13px]">
        <thead>
          <tr class="bg-[var(--app-base)] text-[var(--primary-dark)]">
            <th scope="col" class="px-3 py-2 text-left font-semibold">Client</th>
            <th scope="col" class="px-3 py-2 text-right font-semibold">FMS</th>
            <th scope="col" class="px-3 py-2 text-right font-semibold">iGator</th>
            <th scope="col" class="px-3 py-2 text-left font-semibold">Weather</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let r of tenantRows()" class="border-t border-[var(--app-border)]">
            <th scope="row" class="px-3 py-2 text-left font-medium">
              {{ r.name }}<span *ngIf="r.tenant.test" class="ml-1 whitespace-nowrap rounded border border-[var(--app-border)] px-1 text-[11px] font-normal">test tenant</span>
            </th>
            <td class="whitespace-nowrap px-3 py-2 text-right"
                [ngClass]="{ 'bg-[var(--notification-warning)]/15': r.tenant.crossTenantFmsFields > 0 }">{{ r.tenant.fmsLinked }} / {{ r.tenant.activeFields }}</td>
            <td class="whitespace-nowrap px-3 py-2 text-right"
                [ngClass]="{ 'bg-[var(--notification-warning)]/15': r.tenant.crossTenantTaps > 0 }">{{ r.tenant.igatorLinked }} / {{ r.tenant.activeFields }}</td>
            <td class="px-3 py-2">{{ r.station || 'Not set up' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <ul *ngIf="crossTenantNotes().length > 0" data-cross-tenant class="m-0 flex list-none flex-col gap-1 rounded-md bg-[var(--notification-warning)]/15 px-3 py-2 text-xs">
      <li *ngFor="let note of crossTenantNotes()">{{ note }}</li>
      <li>Shaded cells include these links. Relink them on each field's Field Analysis page.</li>
    </ul>
  </section>
</div>
